import {
  Component,
  Output,
  Input,
  EventEmitter,
  ViewChild,
  OnInit,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import * as moment from 'moment';
import { Moment } from 'moment';

enum PeriodModifier {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last7Days',
  Last30Days = 'last30Days',
  Last1Year = 'last1Year',
}
@Component({
  selector: 'app-moment-period-datepicker',
  templateUrl: './moment-period-datepicker.component.html',
  styleUrls: ['./moment-period-datepicker.component.scss'],
})
export class MomentPeriodDatepickerComponent implements OnInit {
  private _start: Moment;
  private _end: Moment;

  @Input()
  set start(start: Moment) {
    this._start = start;
    this.initSelectedModifier();
  }
  get start(): Moment {
    return this._start;
  }

  @Input()
  set end(end: Moment) {
    this._end = end;
    this.initSelectedModifier();
  }
  get end(): Moment {
    return this._end;
  }

  @Input() label: string;

  @Input() maxEndDate = moment();
  @Input() set periodModifiers(periodModifiers: PeriodModifier[]) {
    if (!periodModifiers) {
      return;
    }
    this._periodModifiers = periodModifiers;
  }

  @Output() startChange = new EventEmitter<Moment>();
  @Output() endChange = new EventEmitter<Moment>();

  @ViewChild('picker') picker: MatDatepicker<Date>;

  public _periodModifiers: PeriodModifier[] = [
    PeriodModifier.Today,
    PeriodModifier.Yesterday,
    PeriodModifier.Last7Days,
    PeriodModifier.Last30Days,
    PeriodModifier.Last1Year,
  ];
  public selectedPeriodModifier: PeriodModifier;
  public isDatePickerMode = false;

  ngOnInit(): void {
    this.initSelectedModifier();
  }

  initSelectedModifier(): void {
    if (!this._start || !this._end) {
      return;
    }

    if (this.selectedPeriodModifier) {
      return;
    }

    for (const modifier of this._periodModifiers) {
      const { start, end } = this.getPeriodForPeriodModifier(modifier);
      if (this._start.isSame(start, 'day') && this._end.isSame(end, 'day')) {
        this.selectedPeriodModifier = modifier;
        break;
      }
    }

    this.isDatePickerMode = !this.selectedPeriodModifier;
  }

  onStartChange(): void {
    if (!this._start) {
      return;
    }

    if (this._end && this._start?.isAfter(this._end)) {
      this._start = this._end.clone();
    }

    this.startChange.emit(this._start);
  }

  onEndChange(): void {
    if (!this._end) {
      return;
    }

    if (this.maxEndDate && this._end?.isAfter(this.maxEndDate)) {
      this._end = this.maxEndDate.clone();
    } else if (this._start && this._end?.isBefore(this._start)) {
      this._end = this._start.clone();
    }

    this.endChange.emit(this._end);
  }

  onPeriodModifierClick(modifier: PeriodModifier): void {
    this.selectedPeriodModifier = modifier;

    const { start, end } = this.getPeriodForPeriodModifier(modifier);
    this._start = start;
    this._end = end;

    this.startChange.emit(this._start);
    this.endChange.emit(this._end);
  }

  getPeriodForPeriodModifier(modifier: PeriodModifier): {
    start: Moment;
    end: Moment;
  } {
    let start: Moment;
    let end: Moment;

    switch (modifier) {
      case PeriodModifier.Today:
        start = moment();
        end = moment();
        break;
      case PeriodModifier.Yesterday:
        start = moment().subtract(1, 'day');
        end = moment().subtract(1, 'day');
        break;
      case PeriodModifier.Last7Days:
        start = moment().subtract(6, 'day');
        end = moment();
        break;
      case PeriodModifier.Last30Days:
        start = moment().subtract(29, 'day');
        end = moment();
        break;
      case PeriodModifier.Last1Year:
        start = moment().subtract(1, 'year');
        end = moment();
        break;
    }

    return { start, end };
  }

  openDatePicker(): void {
    this.picker.open();
    this.isDatePickerMode = true;
  }
}
